import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, Bug } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ErrorState } from '@/components/shared/ErrorState'
import { SeverityBadge } from '@/components/shared/SeverityBadge'
import {
  useAdminVulnerabilities,
  useBulkPatchVulnerabilities,
  type AdminVulnerability,
  type AdminVulnSortKey,
  type VulnerabilitySeverity,
} from '@/hooks/useAdminVulnerabilities'
import { formatDate } from '@/lib/utils'

const EXPLOIT_STATUSES = ['none', 'poc', 'active', 'widespread'] as const
const SEVERITIES: VulnerabilitySeverity[] = ['critical', 'high', 'medium', 'low']

function mostRecentChangedBy(v: AdminVulnerability): string | null {
  const exp = v.exploitationStatusChangedAt
    ? new Date(v.exploitationStatusChangedAt).getTime()
    : -Infinity
  const patch = v.patchAvailableChangedAt
    ? new Date(v.patchAvailableChangedAt).getTime()
    : -Infinity
  if (exp === -Infinity && patch === -Infinity) return null
  if (patch > exp) return v.patchAvailableChangedBy ?? null
  return v.exploitationStatusChangedBy ?? null
}

export function VulnerabilitiesAdminPage() {
  const [search, setSearch] = useState('')
  const [severity, setSeverity] = useState<string>('all')
  const [exploitStatus, setExploitStatus] = useState<string>('all')
  const [patchStatus, setPatchStatus] = useState<'all' | 'patched' | 'unpatched'>(
    'all',
  )
  const [needsReview, setNeedsReview] = useState(false)
  // V2 — CISA-KEV de-escalation filter + sort
  const [recentlyDeescalated, setRecentlyDeescalated] = useState(false)
  const [sort, setSort] = useState<AdminVulnSortKey>('updated_at')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const { data, isLoading, isError, refetch } = useAdminVulnerabilities({
    search: search || undefined,
    severity: severity !== 'all' ? severity : undefined,
    exploitation_status: exploitStatus !== 'all' ? exploitStatus : undefined,
    patch: patchStatus !== 'all' ? patchStatus : undefined,
    needs_review: needsReview || undefined,
    recentlyDeescalated: recentlyDeescalated || undefined,
    sort,
    direction: 'desc',
  })

  const bulkPatch = useBulkPatchVulnerabilities()

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const items = data?.items ?? []
  const allSelected =
    items.length > 0 && items.every((v) => selectedIds.has(v.id))

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) return new Set()
      const next = new Set(prev)
      items.forEach((v) => next.add(v.id))
      return next
    })
  }

  const onBulkMarkPatched = async () => {
    if (selectedIds.size === 0) return
    await bulkPatch.mutateAsync({
      ids: Array.from(selectedIds),
      fields: { patchAvailable: true },
    })
    setSelectedIds(new Set())
  }

  const headerMeta = useMemo(() => {
    if (!data) return null
    return `${data.total} CVE${data.total === 1 ? '' : 's'}`
  }, [data])

  return (
    <main className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Bug className="h-5 w-5 text-destructive" aria-hidden />
            Vulnerabilities
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Admin CRUD + manual overrides. CVSS and severity stay authoritative to NVD.
          </p>
        </div>
        {headerMeta && (
          <Badge variant="outline" className="self-start">
            {headerMeta}
          </Badge>
        )}
      </header>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search CVE or title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            aria-label="Search"
          />
        </div>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="w-36" aria-label="severity">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            {SEVERITIES.map((s) => (
              <SelectItem key={s} value={s} className="capitalize">
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={exploitStatus} onValueChange={setExploitStatus}>
          <SelectTrigger className="w-40" aria-label="exploitation">
            <SelectValue placeholder="Exploitation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All exploitation</SelectItem>
            {EXPLOIT_STATUSES.map((s) => (
              <SelectItem key={s} value={s} className="capitalize">
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={patchStatus}
          onValueChange={(v) => setPatchStatus(v as typeof patchStatus)}
        >
          <SelectTrigger className="w-36" aria-label="patch">
            <SelectValue placeholder="Patch" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All patch status</SelectItem>
            <SelectItem value="patched">Patched</SelectItem>
            <SelectItem value="unpatched">Unpatched</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs">
          <input
            type="checkbox"
            checked={needsReview}
            onChange={(e) => setNeedsReview(e.target.checked)}
            aria-label="Needs review"
          />
          Needs review
        </label>
        <label className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs">
          <input
            type="checkbox"
            checked={recentlyDeescalated}
            onChange={(e) => setRecentlyDeescalated(e.target.checked)}
            aria-label="Recently de-escalated"
          />
          Recently de-escalated
        </label>
        <Select
          value={sort}
          onValueChange={(v) => setSort(v as AdminVulnSortKey)}
        >
          <SelectTrigger className="w-44" aria-label="Sort by">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="updated_at">Most recently updated</SelectItem>
            <SelectItem value="kev_removed_at">KEV removed</SelectItem>
            <SelectItem value="cvss_score">CVSS score</SelectItem>
            <SelectItem value="severity">Severity</SelectItem>
          </SelectContent>
        </Select>

        {selectedIds.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {selectedIds.size} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={onBulkMarkPatched}
              disabled={bulkPatch.isPending}
            >
              {bulkPatch.isPending ? 'Applying…' : 'Mark patched'}
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <section>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : isError ? (
          <ErrorState
            title="Failed to load vulnerabilities"
            onRetry={() => refetch()}
          />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No vulnerabilities match your filters.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all"
                    />
                  </th>
                  <th className="p-2">CVE ID</th>
                  <th className="p-2">Severity</th>
                  <th className="p-2">Exploit Status</th>
                  <th className="p-2">Patch</th>
                  <th className="p-2">Updated</th>
                  <th className="p-2">Source</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((v) => {
                  const lastBy = mostRecentChangedBy(v)
                  return (
                    <tr key={v.id} className="border-t border-border">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(v.id)}
                          onChange={() => toggleOne(v.id)}
                          aria-label={`select ${v.cveId}`}
                        />
                      </td>
                      <td className="p-2">
                        <div className="font-mono font-medium">{v.cveId}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1 max-w-sm">
                          {v.title}
                        </div>
                      </td>
                      <td className="p-2">
                        {v.severity ? (
                          <SeverityBadge severity={v.severity} />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-2 capitalize">{v.exploitationStatus}</td>
                      <td className="p-2">
                        {v.patchAvailable == null
                          ? '—'
                          : v.patchAvailable
                            ? 'Patched'
                            : 'Unpatched'}
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {formatDate(v.updatedAt)}
                      </td>
                      <td className="p-2">
                        {lastBy ? (
                          <Badge variant="secondary" className="font-mono text-xs">
                            {lastBy}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <Link
                          to={`/admin/vulnerabilities/${v.id}/edit`}
                          className="text-xs text-primary hover:underline"
                        >
                          Edit
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  )
}
